import { Observable, throwError, BehaviorSubject } from 'rxjs'
import { catchError, map, tap, switchMap } from 'rxjs/operators'
import { ToastrService } from 'ngx-toastr'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { AppConfig } from '../../app.config'
import { User, UserRolesObject } from './user.object'
import { Store } from '@ngrx/store'
import { AppState, getUserOnce } from 'src/app/@store'
import { ErrorHandlerService } from 'src/app/common/error-handler.service'

/**
 * this handles all http calls for users management
 */
@Injectable({
  providedIn: 'root',
})
export class UsersService {
  usersListUpdated$: BehaviorSubject<void> = new BehaviorSubject(null)

  constructor(
    private http: HttpClient,
    private appConfig: AppConfig,
    private toastr: ToastrService,
    private store: Store<AppState>,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  getUsers(): Observable<User[]> {
    const url: string = this.appConfig.config.server_api.users.all

    return this.http.get(url).pipe(map((response: any[]) => response.map((item) => new User(item))))
  }

  getUser(userId: number): Observable<User> {
    const url: string = `${this.appConfig.config.server_api.users.get}${userId}/`

    return this.http.get(url).pipe(map((response) => new User(response)))
  }

  getAllUserRoles(): Observable<UserRolesObject> {
    const url: string = this.appConfig.config.server_api.users.roles
    return this.http.post<any>(url, {})
  }

  deleteUser(user: User): Observable<object> {
    const url: string = `${this.appConfig.config.server_api.users.delete}${user.id}/`

    return this.http.delete(url).pipe(
      tap(() => {
        const message: string = `User ${user.name} successfully removed`
        console.log(message)
        this.toastr.success(message)
      }),
      catchError((error) => {
        const message: string = `Can't delete user due to some error.`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  addUser(user: User, password: string, roles: string[]): Observable<object> {
    const url: string = this.appConfig.config.server_api.users.add

    const params = {
      name: user.name,
      email: user.email,
      password: password ? password : '',
      roles,
    }

    return this.http.post(url, params).pipe(
      tap(() => {
        const message: string = `User successfully added`
        console.log(message)
        this.toastr.success(message)
      }),
      catchError((error) => {
        const message: string = `Can't add user due to some error.`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  editUser(user: User, password: string, roles: string[]): Observable<object> {
    const url: string = `${this.appConfig.config.server_api.users.edit}${user.id}/`

    const params = {
      name: user.name,
      email: user.email,
      roles,
    }

    if (password !== null) {
      params['password'] = password
    }

    return this.http.patch(url, params).pipe(
      tap(() => {
        const message: string = `User ${user.email} successfully edited`
        console.log(message)
        this.toastr.success(message)
      }),
      catchError((error) => {
        const message: string = `Can't edit user due to some error.`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  setUserProfilePicture(file: File): Observable<object> {
    const formData = new FormData()
    // empty headers are important, because if loading file
    // optimal header will be set by default, no need to manual content-type=multipart/form-data
    const headers = new HttpHeaders()
    const url: string = this.appConfig.config.server_api.users.set_user_profile_picture

    formData.append('profile_picture', file)

    return this.http.post(url, formData, { headers }).pipe(
      switchMap(() => this.store.pipe(getUserOnce)),
      tap((user) => {
        const message = `User profile picture successfully changed.`
        console.log(message)
        this.toastr.success(message)
      }),
      catchError((error) => {
        this.toastr.error(`Server error. Can't change user profile picture.`)
        return throwError(error)
      }),
    )
  }
}
